import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { isElectronApp } from 'app/core/pwa/pwa-env';

export type DesktopOs = 'mac' | 'win' | 'linux';

export type DesktopDownloadResult =
    | 'started'
    | 'unavailable'
    | 'unsupported'
    | 'electron';

export interface DesktopDownloadsConfig {
    enabled: boolean;
    /** Absolute or site-relative base, e.g. `/downloads/` or `https://cdn.example.com/erp/` */
    baseUrl: string;
    files: Partial<Record<DesktopOs, string>>;
}

@Injectable({ providedIn: 'root' })
export class DesktopDownloadService {
    detectOs(): DesktopOs | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const ua = window.navigator.userAgent;
        const platform = window.navigator.platform || '';

        if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) {
            return null;
        }

        if (/Mac|iPhone|iPod|iPad/i.test(platform) || /Mac OS X/i.test(ua)) {
            // iPadOS 13+ can report Mac — treat touch as mobile
            if (navigator.maxTouchPoints > 1) {
                return null;
            }
            return 'mac';
        }

        if (/Win/i.test(platform) || /Windows/i.test(ua)) {
            return 'win';
        }

        if (/Linux/i.test(platform) || /Linux/i.test(ua)) {
            return 'linux';
        }

        return null;
    }

    isDesktopBrowser(): boolean {
        return !isElectronApp() && this.detectOs() !== null;
    }

    config(): DesktopDownloadsConfig | null {
        const cfg = (environment as { desktopDownloads?: DesktopDownloadsConfig })
            .desktopDownloads;
        if (!cfg?.enabled || !cfg.baseUrl) {
            return null;
        }
        return cfg;
    }

    isConfigured(): boolean {
        const cfg = this.config();
        if (!cfg) {
            return false;
        }
        const os = this.detectOs();
        return !!os && !!cfg.files[os];
    }

    downloadUrlFor(os: DesktopOs): string | null {
        const cfg = this.config();
        const file = cfg?.files[os];
        if (!cfg || !file) {
            return null;
        }

        const base = cfg.baseUrl.endsWith('/') ? cfg.baseUrl : `${cfg.baseUrl}/`;
        if (/^https?:\/\//i.test(base)) {
            return `${base}${file}`;
        }

        if (typeof window === 'undefined') {
            return `${base}${file}`;
        }

        return new URL(`${base}${file}`, window.location.origin).toString();
    }

    /** Start download of the Electron installer for the current OS. */
    download(): DesktopDownloadResult {
        if (isElectronApp()) {
            return 'electron';
        }

        const os = this.detectOs();
        if (!os) {
            return 'unsupported';
        }

        const url = this.downloadUrlFor(os);
        if (!url) {
            return 'unavailable';
        }

        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = '';
        anchor.rel = 'noopener';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        return 'started';
    }

    osLabel(os: DesktopOs | null = this.detectOs()): string {
        switch (os) {
            case 'mac':
                return 'macOS';
            case 'win':
                return 'Windows';
            case 'linux':
                return 'Linux';
            default:
                return 'desktop';
        }
    }
}
